import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Settings, Clock, AlertTriangle, MessageSquare, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import api from '../utils/api';
import DeleteConfirmModal from '../components/DeleteConfirmModal';
import { useToast } from '../context/ToastContext';

// Specialized Sidebar for Agents
const AgentChatSidebar = ({
  sessions,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  isCollapsed,
  currentPage,
  totalSessions,
  onPageChange,
  pageSize,
  isLoading
}) => {
  const totalPages = Math.ceil(totalSessions / pageSize);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const timestamp = typeof dateString === 'number' && dateString < 10000000000 ? dateString * 1000 : dateString;
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  return (
    <div style={{
      width: isCollapsed ? '80px' : '320px',
      background: '#ffffff',
      borderRight: '1px solid rgba(0,0,0,0.05)',
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 20,
      position: 'relative'
    }}>
      {/* Sidebar Header */}
      <div style={{
        padding: isCollapsed ? '24px 0' : '24px',
        borderBottom: '1px solid rgba(0,0,0,0.03)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: isCollapsed ? 'center' : 'stretch',
        gap: '16px'
      }}>
        {!isCollapsed && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{
              margin: 0,
              fontSize: '11px',
              fontWeight: '900',
              color: '#111827',
              textTransform: 'uppercase',
              letterSpacing: '0.15em'
            }}>Active Sessions</h2>
            <div style={{
              padding: '4px 8px',
              background: '#f0f7ff',
              borderRadius: '6px',
              fontSize: '10px',
              fontWeight: '800',
              color: '#2563eb'
            }}>
              {totalSessions}
            </div>
          </div>
        )}
        {isCollapsed && <MessageSquare size={20} color="#111827" />}
      </div>

      {/* Session List */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }} className="custom-scrollbar">
        {isLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div className="animate-spin" style={{ width: '20px', height: '20px', border: '2px solid #f3f4f6', borderTop: '2px solid #2563eb', borderRadius: '50%', margin: '0 auto' }} />
          </div>
        ) : sessions.length === 0 ? (
          <div style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#94a3b8',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            No active sessions found
          </div>
        ) : (
          sessions.map((session) => (
            <div
              key={session.session_id}
              onClick={() => onSelectSession(session)}
              style={{
                padding: isCollapsed ? '12px' : '14px 16px',
                background: activeSessionId === session.session_id ? '#f0f7ff' : '#ffffff',
                borderRadius: '16px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'all 0.2s ease',
                border: '1px solid',
                borderColor: activeSessionId === session.session_id ? '#83bbe133' : 'transparent',
                position: 'relative'
              }}
              onMouseOver={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.background = '#f9fafb';
                }
              }}
              onMouseOut={(e) => {
                if (activeSessionId !== session.session_id) {
                  e.currentTarget.style.background = '#ffffff';
                }
              }}
            >
              {/* Status Badges */}
              {session.handoff_status === 'requested' && (
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#ef4444',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: '900',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  zIndex: 5,
                  animation: 'pulse-red 2s infinite'
                }}>
                  Handoff
                </div>
              )}

              {session.handoff_status === 'active' && (
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#22c55e',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: '900',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)',
                  zIndex: 5
                }}>
                  Live
                </div>
              )}

              {session.handoff_status === 'resolved' && (
                <div style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  background: '#94a3b8',
                  color: '#ffffff',
                  fontSize: '9px',
                  fontWeight: '900',
                  padding: '2px 8px',
                  borderRadius: '20px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  boxShadow: '0 4px 12px rgba(148, 163, 184, 0.2)',
                  zIndex: 5
                }}>
                  Resolved
                </div>
              )}

              <MessageSquare size={16} strokeWidth={2.5} style={{ flexShrink: 0, color: session.handoff_status === 'requested' ? '#ef4444' : '#2563eb' }} />

              {!isCollapsed && (
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#111827',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    letterSpacing: '-0.01em'
                  }}>
                    {typeof session.summary === 'string'
                      ? session.summary.replace(/\s*<additional context>[\s\S]*?(?:<\/additional context>|$)/g, '').replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '').trim()
                      : (session.summary || 'New Conversation')}
                  </div>
                  <div style={{
                    fontSize: '10px',
                    color: '#64748b',
                    marginTop: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontWeight: '500',
                    textTransform: 'uppercase'
                  }}>
                    <Clock size={10} strokeWidth={2} />
                    {formatTime(session.updated_at)}
                  </div>
                </div>
              )}


            </div>
          ))
        )}
      </div>

      {/* Pagination Footer */}
      {!isCollapsed && totalPages > 1 && (
        <div style={{
          padding: '16px',
          borderTop: '1px solid rgba(0,0,0,0.03)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px'
        }}>
          <button
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            style={{
              padding: '6px 12px',
              background: '#f9fafb',
              border: '1px solid #f3f4f6',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '700',
              color: currentPage === 1 ? '#cbd5e1' : '#64748b',
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase'
            }}
          >Prev</button>
          <span style={{ fontSize: '10px', fontWeight: '800', color: '#111827' }}>{currentPage} / {totalPages}</span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            style={{
              padding: '6px 12px',
              background: '#f9fafb',
              border: '1px solid #f3f4f6',
              borderRadius: '8px',
              fontSize: '10px',
              fontWeight: '700',
              color: currentPage === totalPages ? '#cbd5e1' : '#64748b',
              cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
              textTransform: 'uppercase'
            }}
          >Next</button>
        </div>
      )}

      <style>{`
        @keyframes pulse-red {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.9; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

const AgentChatInterface = ({ onLogout, user, hideHeader = false }) => {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const getActiveAgentId = useCallback(() => {
    // For non-admin users, always use their own agent ID
    if (user && user.role !== 'admin') {
      return user.agent_id?.toString() || null;
    }
    // For admin, check stored activeAgentId; ignore placeholder defaults
    const stored = localStorage.getItem('activeAgentId');
    const placeholder = '00000000-0000-0000-0000-000000000002';
    if (stored && stored !== 'None' && stored !== 'null' && stored !== placeholder) {
      return stored;
    }
    // Fallback to current user's primary agent ID if available
    return user?.agent_id?.toString() || null;
  }, [user]);

  const [messages, setMessages] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem('agent_interface_session_id');
    if (stored && stored !== 'None' && stored !== 'null') return stored;
    return null;
  });
  const [logoUrl, setLogoUrl] = useState(localStorage.getItem('activeAgentLogo'));
  const [sessions, setSessions] = useState([]);
  const [isSidebarCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalSessions, setTotalSessions] = useState(0);
  const [isSessionsLoading, setIsSessionsLoading] = useState(false);
  const SESSIONS_PER_PAGE = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeletingSession, setIsDeletingSession] = useState(false);

  // Human Handoff State
  const [isHandoffRequested, setIsHandoffRequested] = useState(false);
  const [isHandoffAccepted, setIsHandoffAccepted] = useState(false);
  const [handoffReason, setHandoffReason] = useState('');
  const [isInactive, setIsInactive] = useState(false);
  const isInactiveRef = useRef(false);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchLogo = useCallback(async () => {
    try {
      // Pass agentId explicitly for agents
      const agentId = getActiveAgentId();
      const data = await api.request('/logo/config', { agentId });
      if (data && data.logo_url) {
        // Handle path resolution similar to api.js getCurrentLogo
        let finalUrl = data.logo_url;
        if (finalUrl.startsWith('/uploads')) {
          finalUrl = `${process.env.REACT_APP_API_BASE_URL}${finalUrl}`;
        }
        setLogoUrl(finalUrl);
      } else {
        setLogoUrl('/default.png');
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
      setLogoUrl('/default.png');
    }
  }, [user]);

  const fetchSessions = useCallback(async (page) => {
    try {
      setIsSessionsLoading(true);
      const agentId = getActiveAgentId();
      const fetchPage = page || 1;
      const data = await api.getWidgetSessions(agentId, fetchPage, SESSIONS_PER_PAGE, true);
      setSessions(data.sessions || []);
      setTotalSessions(data.total_count || 0);
      setCurrentPage(data.page || fetchPage);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setIsSessionsLoading(false);
    }
  }, [user]); // Removed currentPage dependency to avoid loop

  // Refs to keep WebSocket stable and prevent reconnection storm
  const sessionIdRef = useRef(sessionId);
  const currentPageRef = useRef(currentPage);
  const fetchSessionsRef = useRef(fetchSessions);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  useEffect(() => {
    fetchSessionsRef.current = fetchSessions;
  }, [fetchSessions]);

  const loadChatHistory = useCallback(async (id = sessionId) => {
    if (!id) return;
    try {
      const agentId = getActiveAgentId();
      const history = await api.getWidgetHistory(id, agentId);
      if (history && history.length > 0) {
        const restored = history
          .filter(msg => msg.message && String(msg.message).trim() !== '')
          .map((msg, idx) => ({
            id: `history_${idx}`,
            text: String(msg.message)
              .replace(/\s*<additional context>[\s\S]*?<\/additional context>\s*/g, '')
              .replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '')
              .trim(),
            sender: msg.type,
            timestamp: new Date(typeof msg.timestamp === 'number' && msg.timestamp < 10000000000 ? msg.timestamp * 1000 : msg.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }),
          }));
        setMessages(restored);
      } else {
        setMessages([]);
      }
      setHistoryLoaded(true);
    } catch (err) {
      console.error('Error loading chat history:', err);
    }
  }, [user, sessionId]);

  // Initial load
  useEffect(() => {
    fetchLogo();
    fetchSessions(1);
  }, [fetchLogo, fetchSessions]);

  // Handle session selection
  useEffect(() => {
    if (sessionId) {
      loadChatHistory();
    }
  }, [sessionId, loadChatHistory]);

  const handleSelectSession = (session) => {
    const id = session.session_id;
    if (id === sessionId) return;
    setSessionId(id);
    localStorage.setItem('agent_interface_session_id', id);
    setMessages([]);
    setHistoryLoaded(false);

    const isRequested = session.handoff_status === 'requested';
    const isAccepted = session.handoff_status === 'active' || session.handoff_status === 'claimed';
    setIsHandoffRequested(isRequested);
    setIsHandoffAccepted(isAccepted);
    setHandoffReason(session.last_reason || session.handoff_reason || 'User requested assistance');
    loadChatHistory(id);
  };

  const handleDeleteSession = (id) => {
    setSessionToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteSession = async () => {
    if (!sessionToDelete) return;
    setIsDeletingSession(true);
    try {
      const agentId = getActiveAgentId();
      await api.deleteWidgetSession(sessionToDelete, agentId);
      setSessions(prev => prev.filter(s => s.session_id !== sessionToDelete));
      if (sessionToDelete === sessionId) {
        setSessionId(null);
        setMessages([]);
      }
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error('Error deleting session:', err);
    } finally {
      setIsDeletingSession(false);
      setSessionToDelete(null);
    }
  };

  // --- WebSocket Real-Time Integration ---
  useEffect(() => {
    if (!user) return;

    let socket = null;
    let reconnectTimeout = null;
    let inactivityTimeout = null;
    let isComponentMounted = true;

    // Reset inactivity timer function
    const resetInactivityTimeout = () => {
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      if (isInactiveRef.current) {
        console.log('🔌 [EasyChat Debug] User active again. Reconnecting WebSocket...');
        setIsInactive(false);
        isInactiveRef.current = false;
        connectWebSocket();
      }
      
      // 30 minutes = 1,800,000ms
      inactivityTimeout = setTimeout(() => {
        console.warn('⚠️ [EasyChat Debug] 30 minutes of inactivity detected. Pausing connection.');
        setIsInactive(true);
        isInactiveRef.current = true;
        if (socket) {
          socket.close();
        }
      }, 1800000);
    };

    // Attach activity listeners
    const events = ['mousemove', 'keypress', 'click', 'scroll'];
    events.forEach(event => window.addEventListener(event, resetInactivityTimeout));

    // Initialize timer on load
    resetInactivityTimeout();

    const connectWebSocket = () => {
      if (!isComponentMounted) return;
      if (isInactiveRef.current) return;

      const agentId = getActiveAgentId();

      // Get token from sessionStorage (following utils/api.js logic)
      let token = null;
      const storedUser = sessionStorage.getItem('user');
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          token = userData.token;
        } catch (e) {
          console.error('Error parsing user data for WS token:', e);
        }
      }

      if (!token) {
        console.warn('⚠️ No auth token found for WebSocket connection');
        return;
      }

      // Determine WS protocol based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsHost = process.env.REACT_APP_API_BASE_URL.replace(/^https?:\/\//, '').replace(/\/api$/, '');
      const wsUrl = `${protocol}//${wsHost}/api/ws/agents?agent_id=${agentId}&token=${token}`;

      console.log('🔌 Connecting to WebSocket:', wsUrl);

      socket = new WebSocket(wsUrl);

      socket.onopen = () => {
        console.log('✅ WebSocket Connected');
      };

      // ELITE: Track last processed events to prevent duplicate UI flashes
      const lastEventRef = { id: null, type: null, time: 0 };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Deduplicate events occurring within 100ms
          const now = Date.now();
          if (data.session_id === lastEventRef.id && data.type === lastEventRef.type && (now - lastEventRef.time) < 100) {
            return;
          }
          lastEventRef.id = data.session_id;
          lastEventRef.type = data.type;
          lastEventRef.time = now;

          console.log('📥 WS Message Received:', data);

          const currentActiveSessionId = sessionIdRef.current;

          switch (data.type) {
            case 'SESSION_STATUS':
              console.log('📊 Session status update:', data);
              if (data.session_id) {
                // ELITE: Instant local state update to show badge without waiting for fetchSessions
                if (data.status === 'requested') {
                  setSessions(prev => prev.map(s =>
                    s.session_id === data.session_id
                      ? { ...s, handoff_status: 'requested', last_reason: data.reason }
                      : s
                  ));

                  if (data.session_id === currentActiveSessionId) {
                    setIsHandoffRequested(true);
                    setHandoffReason(data.reason || 'Handoff requested');
                  }
                }

                // Still fetch to sync everything else (summary, timestamps)
                if (fetchSessionsRef.current) {
                  fetchSessionsRef.current(currentPageRef.current);
                }
              }
              break;

            case 'HANDOFF_REQUESTED':
              // Refresh sessions to show the badge
              if (fetchSessionsRef.current) {
                fetchSessionsRef.current(currentPageRef.current);
              }
              // If it's the current session, update local state
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffRequested(true);
                setHandoffReason(data.reason || 'User requested assistance');
              }
              break;

            case 'NEW_MESSAGE':
              // If this message is for the current active session, append it
              if (data.session_id === currentActiveSessionId) {
                setMessages(prev => {
                  const cleanedText = String(data.message.content)
                    .replace(/\s*<additional context>[\s\S]*?<\/additional context>\s*/g, '')
                    .replace(/\s*\{[\s\S]*?"(?:filename|userid|category)"[\s\S]*?\}\s*/g, '')
                    .trim();
                  // Check if message already exists (to avoid duplicates if polling also runs or local fallback updates)
                  const exists = prev.some(m => m.text.trim() === cleanedText);
                  if (exists) return prev;

                  return [...prev, {
                    id: Date.now() + Math.random(),
                    text: cleanedText,
                    sender: data.message.role,
                    timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
                  }];
                });
              }
              // Also update sessions list to show latest summary/timestamp
              if (fetchSessionsRef.current) {
                fetchSessionsRef.current(currentPageRef.current);
              }
              break;

            case 'SESSION_CLAIMED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(true);
                setIsHandoffRequested(false);
              }
              if (fetchSessionsRef.current) {
                fetchSessionsRef.current(currentPageRef.current);
              }
              break;

            case 'SESSION_RESOLVED':
              if (data.session_id === currentActiveSessionId) {
                setIsHandoffAccepted(false);
                setIsHandoffRequested(false);
              }
              if (fetchSessionsRef.current) {
                fetchSessionsRef.current(currentPageRef.current);
              }
              break;

            default:
              break;
          }
        } catch (err) {
          console.error('❌ WS Parsing Error:', err);
        }
      };

      socket.onclose = () => {
        if (!isComponentMounted) return;
        if (isInactiveRef.current) {
          console.log('🔌 WebSocket disconnected due to inactivity. Will not auto-reconnect.');
          return;
        }
        console.log('🔌 WebSocket Disconnected. Reconnecting...');
        reconnectTimeout = setTimeout(connectWebSocket, 3000);
      };

      socket.onerror = (err) => {
        console.error('❌ WebSocket Error:', err);
        socket.close();
      };
    };

    connectWebSocket();

    return () => {
      isComponentMounted = false;
      if (socket) socket.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (inactivityTimeout) clearTimeout(inactivityTimeout);
      events.forEach(event => window.removeEventListener(event, resetInactivityTimeout));
    };
  }, [user]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !isHandoffAccepted) return;

    const currentInput = inputMessage;
    setInputMessage('');
    setIsLoading(true);

    try {
      const agentId = getActiveAgentId();
      await api.post('/widget/chat/agent-message', {
        session_id: sessionId,
        message: currentInput
      }, { agentId });

      // Proactive premium local fallback: instantly append sent message to the messages array!
      setMessages(prev => {
        const exists = prev.some(m => m.text.trim() === currentInput.trim());
        if (exists) return prev;
        return [...prev, {
          id: Date.now() + Math.random(),
          text: currentInput,
          sender: 'human_agent',
          timestamp: new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })
        }];
      });

      // Refresh session list to update latest timestamp/summary
      fetchSessions(currentPage);
    } catch (error) {
      console.error('Chat error:', error);
      // Restore input on failure
      setInputMessage(currentInput);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTakeOver = useCallback(async () => {
    try {
      const agentId = getActiveAgentId();
      await api.post('/widget/handoff/takeover', {
        session_id: sessionId
      }, { agentId });
      setIsHandoffAccepted(true);
      setIsHandoffRequested(false);
      // Refresh sessions to show the 'Live' status in sidebar
      fetchSessions(currentPage);
    } catch (err) {
      console.error('Takeover error:', err);
    }
  }, [sessionId, user]);

  const handleResolveHandoff = useCallback(async () => {
    try {
      const agentId = getActiveAgentId();
      await api.post('/widget/handoff/resolve', {
        session_id: sessionId
      }, { agentId });
      setIsHandoffRequested(false);
      setIsHandoffAccepted(false);
      setHandoffReason('');
      // Immediately refresh sessions list to update sidebar badges
      fetchSessions(currentPage);
    } catch (err) {
      console.error('Resolve error:', err);
    }
  }, [sessionId, user]);

  // Sync handoff state from session list
  useEffect(() => {
    const currentSession = sessions.find(s => s.session_id === sessionId);
    if (currentSession) {
      setIsHandoffRequested(currentSession.handoff_status === 'requested');
      setIsHandoffAccepted(currentSession.handoff_status === 'active');
      setHandoffReason(currentSession.handoff_reason || '');
    }
  }, [sessionId, sessions]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ height: hideHeader ? 'calc(100vh - 80px)' : '100vh', display: 'flex', flexDirection: 'column', background: '#f9fafb', overflow: 'hidden' }}>
      {/* Agent Header */}
      {!hideHeader && (
        <div style={{
          padding: '16px 24px',
          background: '#0f172a',
          borderBottom: '1px solid rgba(255,255,255,0.05)',
          zIndex: 30
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            maxWidth: '1800px',
            margin: '0 auto',
            width: '100%'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justify: 'center', padding: '8px' }}>
                <img
                  src={logoUrl || '/default.png'}
                  alt="Logo"
                  style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                  onError={(e) => e.target.src = '/default.png'}
                />
              </div>
              <h1 style={{ margin: 0, color: 'white', fontSize: '14px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', fontStyle: '' }}>Agent Command Center</h1>
            </div>

            <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ color: '#ffffff', fontSize: '12px', fontWeight: '900', letterSpacing: '0.05em' }}>
                  {user?.agent_id && <span style={{ color: '#94a3b8', marginRight: '4px' }}>[{user.agent_id}]</span>}
                  {user?.username}
                </div>
                <div style={{ color: '#94a3b8', fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                  {(user?.role === 'admin') ? 'Admin' : 'Agent'}
                </div>
              </div>

              <button onClick={onLogout} style={{ padding: '8px 16px', background: '#ef4444', border: 'none', borderRadius: '10px', color: 'white', fontSize: '11px', fontWeight: '800', cursor: 'pointer', textTransform: 'uppercase', fontStyle: '' }}>
                Logout
              </button>

              {(user?.role === 'admin') && (
                <button
                  onClick={() => navigate('/admin')}
                  style={{
                    padding: '8px 16px',
                    background: '#4f46e5',
                    border: 'none',
                    borderRadius: '10px',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '800',
                    cursor: 'pointer',
                    textTransform: 'uppercase',
                    fontStyle: '',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
                  }}
                >
                  <Settings size={14} />
                  Admin Panel
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <AgentChatSidebar
          sessions={sessions}
          activeSessionId={sessionId}
          onSelectSession={handleSelectSession}
          onDeleteSession={handleDeleteSession}
          isCollapsed={isSidebarCollapsed}
          currentPage={currentPage}
          totalSessions={totalSessions}
          onPageChange={fetchSessions}
          pageSize={SESSIONS_PER_PAGE}
          isLoading={isSessionsLoading}
        />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f8fafc', position: 'relative' }}>

          {/* Inactivity Pause Banner */}
          {isInactive && (
            <div style={{
              padding: '16px 24px',
              background: '#fffbeb',
              borderBottom: '1px solid #fde68a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#92400e' }}>
                    Connection Paused
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#b45309', fontWeight: '500' }}>
                    You have been disconnected due to 10 minutes of inactivity. Click Reconnect to resume.
                  </p>
                </div>
              </div>
              <button 
                onClick={() => {
                  window.dispatchEvent(new Event('mousemove'));
                }} 
                style={{ padding: '10px 24px', background: '#d97706', color: 'white', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', cursor: 'pointer', outline: 'none' }}
              >
                Reconnect
              </button>
            </div>
          )}

          {/* Neural Handoff Takeover Banner */}
          {(isHandoffRequested || isHandoffAccepted) && (
            <div style={{
              padding: '16px 24px',
              background: isHandoffAccepted ? '#f0fdf4' : '#fef2f2',
              borderBottom: `1px solid ${isHandoffAccepted ? '#dcfce7' : '#fee2e2'}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              animation: 'slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              zIndex: 20
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: isHandoffAccepted ? '#22c55e' : '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                  {isHandoffAccepted ? <Clock size={20} /> : <AlertTriangle size={20} />}
                </div>
                <div>
                  <h4 style={{ margin: 0, fontSize: '11px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.1em', color: isHandoffAccepted ? '#166534' : '#991b1b' }}>
                    {isHandoffAccepted ? 'Human Agent Active' : 'Human Handoff Requested'}
                  </h4>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', color: isHandoffAccepted ? '#15803d' : '#b91c1c', fontWeight: '500' }}>
                    {isHandoffAccepted ? 'You are now controlling this communication channel.' : `Reason: ${handoffReason}`}
                  </p>
                </div>
              </div>

              {!isHandoffAccepted ? (
                <button onClick={handleTakeOver} style={{ padding: '10px 24px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', cursor: 'pointer' }}>
                  Confirm Takeover
                </button>
              ) : (
                <button onClick={handleResolveHandoff} style={{ padding: '10px 24px', background: '#22c55e', color: 'white', border: 'none', borderRadius: '12px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', cursor: 'pointer' }}>
                  Resolve
                </button>
              )}
            </div>
          )}

          {/* Messages Area */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '32px 5%' }} className="custom-scrollbar">
            {!sessionId ? (
              <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#94a3b8', fontSize: '14px' }}>
                Select a session to monitor
              </div>
            ) : !historyLoaded ? (
              <div style={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div className="animate-spin" style={{ width: '30px', height: '30px', border: '3px solid #f3f4f6', borderTop: '3px solid #2563eb', borderRadius: '50%' }} />
              </div>
            ) : (
              messages.map((message) => (
                <div key={message.id} style={{ display: 'flex', justifyContent: (message.sender === 'user' || message.sender === 'human') ? 'flex-start' : 'flex-end', marginBottom: '24px' }}>
                  <div style={{ maxWidth: '80%', background: (message.sender === 'user' || message.sender === 'human') ? '#ffffff' : '#eff6ff', padding: '12px 20px', borderRadius: '16px', boxShadow: '0 4px 12px rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)', wordBreak: 'break-word', overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                    <div style={{ fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', color: '#94a3b8', marginBottom: '4px' }}>{message.sender}</div>
                    <div style={{ fontSize: '14px', lineHeight: '1.5', color: '#111827' }}>
                      <ReactMarkdown>{message.text}</ReactMarkdown>
                    </div>
                    <div style={{ fontSize: '10px', color: '#cbd5e1', marginTop: '4px', textAlign: 'right' }}>{message.timestamp}</div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div style={{ padding: '24px 5%', borderTop: '1px solid rgba(0,0,0,0.05)', background: 'white' }}>
            <div style={{ position: 'relative', display: 'flex', gap: '12px', alignItems: 'center' }}>
              <div style={{ flex: 1, position: 'relative' }}>
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={isHandoffAccepted ? "Type message as human agent..." : "Take over session to send messages"}
                  disabled={!isHandoffAccepted}
                  style={{
                    width: '100%',
                    padding: '16px 24px',
                    paddingRight: '60px',
                    borderRadius: '16px',
                    border: '1px solid #e5e7eb',
                    background: isHandoffAccepted ? '#ffffff' : '#f9fafb',
                    fontSize: '14px',
                    outline: 'none',
                    resize: 'none',
                    height: '56px',
                    cursor: isHandoffAccepted ? 'text' : 'not-allowed'
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!isHandoffAccepted || !inputMessage.trim()}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: isHandoffAccepted ? '#2563eb' : '#cbd5e1',
                    color: 'white',
                    border: 'none',
                    cursor: isHandoffAccepted ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Send size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDeleteSession}
        isLoading={isDeletingSession}
        title="Delete this Chat?"
        message="This action will permanently terminate this conversation history. Proceed?"
      />

      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 0;
          background: transparent;
        }
      `}</style>
    </div>
  );
};

export default AgentChatInterface;
